import * as React from "react"

import { TagManagerContext } from "../tagManager"
import dispatchMiddleware from "./middleware"
import {
  ContactFormActionType,
  ContactFormResultType,
} from "../../../common/enums"
import { SendEmailData } from "../../../api/services"

export type SubjectType =
  | "general_enquiry"
  | "business_solution"
  | "strategy"
  | "product_development"
  | "digital_content"

export type ModalState =
  | ""
  | "captcha_failed"
  | "no_captcha"
  | "waiting"
  | "success"
  | "failed"

// State
export interface State {
  data: SendEmailData
  defaultSubject: SubjectType
  modalState: ModalState
  modalMessage: string
  captchaToken: string | null
  isModalOpen: boolean
  isLoading: boolean
  isVerified: boolean
  isSubmitted: boolean
}

const initialState: State = {
  data: {
    from: "",
    to: "info@iphupha.co.za",
    name: "",
    subject: "general_enquiry",
    message: "",
  },
  defaultSubject: "general_enquiry",
  modalState: "",
  modalMessage: "",
  captchaToken: null,
  isModalOpen: false,
  isLoading: false,
  isVerified: false,
  isSubmitted: false,
}

// Creat Context Object
export const ContactFormContext = React.createContext<
  [
    State,
    React.Dispatch<{
      type: ContactFormActionType | ContactFormResultType
      payload?: any
    }>
  ]
>([initialState, () => null])

const reducer = (state: State, action: any) => {
  switch (action.type) {
    case ContactFormResultType.SetLoading: {
      const updatedState = {
        ...state,
        isLoading: action.payload.isLoading,
      }

      return updatedState
    }

    case ContactFormResultType.SetVerified: {
      const updatedState = {
        ...state,
        isVerified: action.payload.isVerified,
      }

      return updatedState
    }

    case ContactFormResultType.SetSubmitted: {
      const updatedState = {
        ...state,
        isSubmitted: action.payload.isSubmitted,
      }

      return updatedState
    }

    case ContactFormResultType.SetDefaultSubject: {
      const updatedState = {
        ...state,
        data: {
          ...state.data,
          subject: action.payload.defaultSubject,
        },
        defaultSubject: action.payload.defaultSubject,
      }

      return updatedState
    }

    case ContactFormResultType.SetCaptchaToken: {
      const updatedState = {
        ...state,
        captchaToken: action.payload.captchaToken,
      }

      return updatedState
    }

    case ContactFormResultType.SetModalOpen: {
      const updatedState = {
        ...state,
        isModalOpen: action.payload.isModalOpen,
      }

      return updatedState
    }

    case ContactFormResultType.SetModalState: {
      let modalMessage = ""
      switch (action.payload.modalState as ModalState) {
        case "captcha_failed":
          modalMessage =
            "ReCAPTCHA (Security Validation) verification was unsuccessful. Please try again."
          break
        case "no_captcha":
          modalMessage =
            "Please complete the ReCAPTCHA (Security Validation) to verify that you are not a bot before sending the message."
          break
        case "waiting":
          modalMessage = "Sending your message. Please wait."
          break
        case "success":
          modalMessage =
            "Hey! Your message has been sent. Please find a confirmation email in your inbox."
          break
        case "failed":
          modalMessage =
            "Something went wrong while trying to send your message, please try again later."
          break
        default:
          modalMessage = ""
          break
      }

      const updatedState = {
        ...state,
        modalState: action.payload.modalState,
        modalMessage,
      }

      return updatedState
    }

    case ContactFormResultType.SetDataFrom: {
      const updatedState = {
        ...state,
        data: {
          ...state.data,
          from: action.payload.from,
        },
      }

      return updatedState
    }

    case ContactFormResultType.SetDataTo: {
      const updatedState = {
        ...state,
        data: {
          ...state.data,
          to: action.payload.to,
        },
      }

      return updatedState
    }

    case ContactFormResultType.SetDataName: {
      const updatedState = {
        ...state,
        data: {
          ...state.data,
          name: action.payload.name,
        },
      }

      return updatedState
    }

    case ContactFormResultType.SetDataSubject: {
      const updatedState = {
        ...state,
        data: {
          ...state.data,
          subject: action.payload.subject,
        },
      }

      return updatedState
    }

    case ContactFormResultType.ResetDataSubject: {
      const updatedState = {
        ...state,
        data: {
          ...state.data,
          subject: state.defaultSubject,
        },
      }

      return updatedState
    }

    case ContactFormResultType.SetDataCompany: {
      const updatedState = {
        ...state,
        data: {
          ...state.data,
          company: action.payload.company,
        },
      }

      return updatedState
    }

    case ContactFormResultType.SetDataMessage: {
      const updatedState = {
        ...state,
        data: {
          ...state.data,
          message: action.payload.message,
        },
      }

      return updatedState
    }

    case ContactFormResultType.ResetDataMessage: {
      const updatedState = {
        ...state,
        data: {
          ...state.data,
          message: "",
        },
      }

      return updatedState
    }

    default:
      throw new Error()
  }
}

type ContactFormContextProviderProps = {
  children: React.ReactNode
}

// Create a provider for components to consume and subscribe to changes
export const ContactFormContextProvider = ({
  children,
}: ContactFormContextProviderProps) => {
  const tagManager = React.useContext(TagManagerContext)
  const [state, dispatch] = React.useReducer(reducer, initialState)

  return (
    <ContactFormContext.Provider
      value={[state, dispatchMiddleware(dispatch, tagManager)]}
    >
      {children}
    </ContactFormContext.Provider>
  )
}
